import { useEffect } from 'react';
import { getCookieValue, setCookieValue } from '@lingoda/cookie';
import {
    useIsValidLifetimeCouponQuery,
    useUserSubscriptionsSuspenseQuery,
} from '@lingoda/monolith-graphql';

const COUPON_COOKIE_NAME = 'couponCode';

type LifetimeCouponResult = { loading: true } | { loading: false; couponCode?: string };

export const useGetLifetimeDiscountCoupon = (): LifetimeCouponResult => {
    const cookieCoupon = getCookieValue(COUPON_COOKIE_NAME);

    const {
        data: { subscriptions },
    } = useUserSubscriptionsSuspenseQuery();

    const subscriptionCoupon = subscriptions.currentSubscription?.couponCode;

    const { data, loading: loadingCookieCouponData } = useIsValidLifetimeCouponQuery({
        variables: {
            couponCode: cookieCoupon ?? '',
        },
        skip: !cookieCoupon,
    });

    const isValidLifetimeCookieCoupon = !loadingCookieCouponData && !!data?.isValidLifetimeCoupon;

    const { data: subscriptionCouponData, loading: loadingSubscriptionCouponData } =
        useIsValidLifetimeCouponQuery({
            variables: { couponCode: subscriptionCoupon ?? '' },
            skip: !subscriptionCoupon || isValidLifetimeCookieCoupon,
        });

    const isValidSubscriptionLifetimeCoupon =
        !loadingSubscriptionCouponData && !!subscriptionCouponData?.isValidLifetimeCoupon;

    let couponToUse: string | undefined;

    if (cookieCoupon && isValidLifetimeCookieCoupon) {
        couponToUse = cookieCoupon;
    } else if (subscriptionCoupon && isValidSubscriptionLifetimeCoupon) {
        couponToUse = subscriptionCoupon;
    }

    const shouldSetCookieCoupon =
        isValidSubscriptionLifetimeCoupon && subscriptionCoupon !== cookieCoupon;

    useEffect(() => {
        if (shouldSetCookieCoupon && subscriptionCoupon) {
            setCookieValue(COUPON_COOKIE_NAME, subscriptionCoupon);
        }
    }, [shouldSetCookieCoupon, subscriptionCoupon]);

    return loadingCookieCouponData || loadingSubscriptionCouponData
        ? { loading: true }
        : { loading: false, couponCode: couponToUse };
};
